// frontend/src/components/PixRecurrenceActions.js
//
// Ações sobre uma recorrência Pix, na janela de detalhes da página de cobranças Pix (ETAPA 5b):
//   "Cancelar recorrência"        (POST /api/pix/recurrences/:id/cancel)
//   "Retentar cobrança inicial"   (POST /api/pix/recurrences/:id/retry-cob; só jornadas 3 e 4 ainda sem cobrança)
// São ações reais no Santander: cada uma pede confirmação, e o servidor confere a permissão, nunca repete sozinho e confere
// no Pix o que aconteceu. Os botões só aparecem para quem tem a permissão (mesma regra do servidor).

import React, { useRef, useState } from 'react';
import { AlertTriangle, Ban, RotateCcw, X, Check } from 'lucide-react';
import { modalidadesPorUsuario } from '../services/modalidades';
import { cancelPixRecurrence, retryPixCob } from '../services/pixApi';
import './PixRecurrenceActions.css';

const TERMINAL_STATUSES = new Set(['CANCELED', 'CANCELLED', 'CANCELADA', 'CANCELADO', 'REJECTED', 'REJEITADA', 'EXPIRED', 'EXPIRADA', 'COMPLETED', 'CONCLUIDA', 'FAILED', 'FALHA', 'INACTIVE']);
const normalizeId = (id) => String(id || '').toLowerCase().replace(/_/g, '-');
const isOpenStatus = (status) => !TERMINAL_STATUSES.has(String(status || '').toUpperCase());
const brl = (n) => Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }).replace(/\u00a0/g, ' ');

/** Mesma regra do servidor: Admin, ou quem usa a modalidade Pix e tem a permissão "gerar-cobranca". */
export function canManageRecurrences(user) {
  if (!user) return false;
  const roles = (Array.isArray(user.roles) ? user.roles : (user.roles ? [user.roles] : [])).map((r) => String(r).toLowerCase());
  if (roles.includes('admin')) return true;
  const permissions = (Array.isArray(user.permissions) ? user.permissions : []).map(normalizeId);
  return modalidadesPorUsuario(user).includes('pix') && permissions.includes('gerar-cobranca');
}

/** Texto para o usuário e se a lista precisa ser atualizada, a partir do erro de uma chamada. */
function describe(error) {
  switch (error && error.code) {
    case 'PIX_ACCESS_DENIED':
      return { text: 'Seu usuário não tem permissão para esta ação. Peça a um administrador a permissão "Gerar Cobrança" e a modalidade Pix.', refresh: false };
    case 'ALREADY_CLOSED':
      return { text: 'Esta recorrência já está encerrada no Pix. A lista será atualizada.', refresh: true };
    case 'ALREADY_HAS_CHARGE':
      return { text: 'Esta recorrência já tem a cobrança inicial criada. A lista será atualizada.', refresh: true };
    case 'PIX_NOT_FOUND':
      return { text: 'Esta recorrência não existe mais no Pix. Atualize a lista.', refresh: true };
    case 'PIX_REJECTED':
      return { text: `O Pix recusou: ${error.message}`, refresh: false };
    case 'PIX_UNREACHABLE':
    case 'PIX_TIMEOUT':
      return { text: 'O Pix não respondeu. Nada foi alterado; tente de novo em instantes.', refresh: false };
    case 'PIX_NOT_CONFIGURED':
      return { text: 'A integração com o Pix ainda não está configurada no servidor. Avise o administrador.', refresh: false };
    default:
      return { text: (error && error.message) || 'Não foi possível concluir. Tente novamente.', refresh: false };
  }
}

/**
 * @param {Object} props
 * @param {Object} props.recurrence recorrência do Pix (id, status, jornada, amount, metadata, client).
 * @param {Object} [props.user] usuário logado (roles, permissions e modalidades).
 * @param {Function} [props.onChanged] chamada ao fechar depois de uma mudança, para a página atualizar a lista.
 */
export default function PixRecurrenceActions({ recurrence, user, onChanged }) {
  const [dialog, setDialog] = useState(null); // null | 'cancel' | 'retry'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const [refresh, setRefresh] = useState(false);
  const runningRef = useRef(false);

  if (!recurrence || !canManageRecurrences(user) || !isOpenStatus(recurrence.status)) return null;
  const canRetry = [3, 4].includes(Number(recurrence.jornada)) && !(recurrence.metadata && recurrence.metadata.txidCobranca);
  const clientName = String((recurrence.client && recurrence.client.name) || 'Cliente removido');

  const openDialog = (kind) => { setDialog(kind); setError(''); setDone(''); setRefresh(false); };
  const closeDialog = () => {
    if (busy) return;
    const changed = refresh || !!done;
    setDialog(null); setError(''); setDone(''); setRefresh(false);
    if (changed && onChanged) onChanged();
  };

  const run = async () => {
    if (runningRef.current) return; // clique duplo: uma só chamada
    runningRef.current = true;
    setBusy(true);
    setError('');
    try {
      const answer = dialog === 'cancel' ? await cancelPixRecurrence(recurrence.id) : await retryPixCob(recurrence.id);
      const base = dialog === 'cancel' ? 'Recorrência cancelada no Pix.' : 'Cobrança inicial criada no Pix.';
      setDone(answer && answer.recovered ? `${base} A conexão caiu durante a operação, mas confirmei no Pix que ela foi concluída.` : base);
    } catch (failure) {
      const info = describe(failure);
      setError(info.text);
      if (info.refresh) setRefresh(true);
    } finally {
      runningRef.current = false;
      setBusy(false);
    }
  };

  const isCancel = dialog === 'cancel';
  return (
    <div className="pixa">
      <div className="pixa-botoes">
        {canRetry && <button type="button" className="pixa-btn" onClick={() => openDialog('retry')}><RotateCcw size={15} /> Retentar cobrança inicial</button>}
        <button type="button" className="pixa-btn pixa-btn-perigo" onClick={() => openDialog('cancel')}><Ban size={15} /> Cancelar recorrência</button>
      </div>

      {dialog && (
        <div className="pixa-fundo" onClick={closeDialog}>
          <div className="pixa-modal" role="dialog" aria-modal="true" aria-labelledby="pixa-titulo" onClick={(e) => e.stopPropagation()}>
            <div className="pixa-topo">
              <h3 id="pixa-titulo">{isCancel ? 'Cancelar recorrência' : 'Retentar cobrança inicial'}</h3>
              <button type="button" className="pixa-fechar" onClick={closeDialog} aria-label="Fechar" disabled={busy}><X size={18} /></button>
            </div>
            <dl className="pixa-resumo">
              <div><dt>Cliente</dt><dd>{clientName}</dd></div>
              <div><dt>Valor</dt><dd>{brl(recurrence.amount)}</dd></div>
              <div><dt>Jornada</dt><dd>{recurrence.jornada || '—'}</dd></div>
            </dl>
            {!done && (isCancel ? (
              <p className="pixa-aviso"><AlertTriangle size={16} /> Isto cancela a recorrência <strong>no Santander</strong>: o cliente deixa de ser cobrado automaticamente. Não dá para desfazer por aqui.</p>
            ) : (
              <p className="pixa-aviso"><AlertTriangle size={16} /> Tenta criar de novo a cobrança inicial no Santander (jornada 3: pagamento imediato; jornada 4: com vencimento). Use só se o Pix avisou que ela falhou ao criar a recorrência.</p>
            ))}
            {done && <p className="pixa-ok" role="status"><Check size={16} /> {done}</p>}
            {error && <p className="pixa-erro" role="alert">{error}</p>}
            <div className="pixa-acoes">
              {done || refresh
                ? <button type="button" className="pixa-btn pixa-btn-primario" onClick={closeDialog}>Fechar</button>
                : (
                  <>
                    <button type="button" className="pixa-btn" onClick={closeDialog} disabled={busy}>Voltar</button>
                    <button type="button" className={`pixa-btn ${isCancel ? 'pixa-btn-perigo-cheio' : 'pixa-btn-primario'}`} onClick={run} disabled={busy}>
                      {busy ? (isCancel ? 'Cancelando...' : 'Tentando...') : (isCancel ? 'Cancelar recorrência' : 'Retentar cobrança')}
                    </button>
                  </>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
